import { zodResolver } from '@hookform/resolvers/zod';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import WalletRounded from '@mui/icons-material/WalletRounded';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { keys, usePaymentMethods, type PaymentMethod } from './kit/hooks';
import { METHOD_KINDS, type MethodKind } from './kit/labels';
import { METHOD_ICONS } from './kit/MethodSelect';
import { ErrorState } from './kit/ui';
import { methodSchema, type MethodFormValues } from './schemas';

/** Payment methods (Cash, Bank transfer, …) and the cash/bank account each one posts to. */
export function MethodsTab() {
  const { can } = useAuth();
  const manage = can('settings.manage');
  const methods = usePaymentMethods();
  const notify = useNotify();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PaymentMethod | 'new' | null>(null);

  const toggle = useMutation({
    mutationFn: (m: PaymentMethod) =>
      unwrap(
        api.PATCH('/payment-methods/{id}', {
          params: { path: { id: m.id } },
          body: { isActive: !m.isActive, version: m.version },
        }),
      ),
    onSuccess: async (m) => {
      notify.success(`${m.name} ${m.isActive ? 'turned on' : 'turned off'}`);
      await qc.invalidateQueries({ queryKey: keys.methods });
    },
    onError: (e) => notify.error(e),
  });

  if (methods.isError)
    return <ErrorState error={methods.error} onRetry={() => void methods.refetch()} />;

  const rows = methods.data?.data ?? [];

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography color="text.secondary" variant="body2" sx={{ maxWidth: 560 }}>
          Each method posts to a cash or bank account. Turned-off methods stay on old payments but
          can’t be picked for new ones.
        </Typography>
        {manage && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setEditing('new')}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
          >
            Add method
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
        }}
      >
        {methods.isLoading &&
          [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={112} />)}
        {rows.map((m) => (
          <Card
            key={m.id}
            sx={{
              p: 2.25,
              opacity: m.isActive ? 1 : 0.6,
              transition: 'box-shadow 160ms, opacity 160ms',
              '&:hover': { boxShadow: '0 12px 24px -8px rgba(16,24,40,0.12)' },
            }}
          >
            <Stack direction="row" spacing={1.75} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '12px',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'primary.main',
                  bgcolor: 'color-mix(in srgb, var(--mui-palette-primary-main) 10%, transparent)',
                }}
              >
                {METHOD_ICONS[m.kind]}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 650 }}>
                  {m.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {METHOD_KINDS[m.kind]} · posts to {m.accountName}
                </Typography>
              </Box>
              {manage && (
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => setEditing(m)}
                      aria-label={`Edit ${m.name}`}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={m.isActive ? 'Turn off' : 'Turn on'}>
                    <Switch
                      checked={m.isActive}
                      disabled={toggle.isPending}
                      onChange={() => toggle.mutate(m)}
                      slotProps={{ input: { 'aria-label': `${m.name} active` } }}
                    />
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Card>
        ))}
      </Box>
      {!methods.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            icon={<WalletRounded />}
            title="No payment methods"
            body="Add how you take and make payments — cash, bank transfer, card or cheque."
            action={manage ? { label: 'Add method', onClick: () => setEditing('new') } : undefined}
          />
        </Card>
      )}
      {editing && (
        <MethodDialog
          method={editing === 'new' ? null : editing}
          methods={rows}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function MethodDialog({
  method,
  methods,
  onClose,
}: {
  method: PaymentMethod | null;
  methods: readonly PaymentMethod[];
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const { can } = useAuth();
  // The chart of accounts needs accounting permissions; without them, offer the accounts that
  // existing methods already use.
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => unwrap(api.GET('/accounts')),
    enabled: can('accounts.manage') || can('journal.view') || can('ledger.view'),
    retry: false,
  });
  const options = useMemo(() => {
    const fromChart = (accounts.data?.data ?? [])
      .filter((a) => a.isPaymentAccount && a.isActive)
      .map((a) => ({ id: a.id, name: `${a.code} · ${a.name}` }));
    if (fromChart.length) return fromChart;
    const seen = new Map<string, string>();
    for (const m of methods) seen.set(m.accountId, m.accountName);
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [accounts.data, methods]);

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<MethodFormValues>({
    resolver: zodResolver(methodSchema),
    defaultValues: {
      name: method?.name ?? '',
      kind: method?.kind ?? 'BANK_TRANSFER',
      accountId: method?.accountId ?? '',
    },
  });

  const save = useMutation({
    mutationFn: (v: MethodFormValues) =>
      method
        ? unwrap(
            api.PATCH('/payment-methods/{id}', {
              params: { path: { id: method.id } },
              body: { ...v, version: method.version },
            }),
          )
        : unwrap(api.POST('/payment-methods', { body: v })),
    onSuccess: async (m) => {
      notify.success(method ? `${m.name} saved` : `${m.name} added`);
      await qc.invalidateQueries({ queryKey: keys.methods });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const d of e.details) {
          const field = d.path.split('.').pop();
          if (field === 'name' || field === 'kind' || field === 'accountId')
            setError(field, { message: d.message });
        }
      notify.error(e);
    },
  });

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <form noValidate onSubmit={(e) => void handleSubmit((v) => save.mutate(v))(e)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {method ? `Edit ${method.name}` : 'Add payment method'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              {...register('name')}
              label="Name"
              autoFocus
              placeholder="e.g. JazzCash, Meezan Bank"
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <Controller
              control={control}
              name="kind"
              render={({ field }) => (
                <TextField select label="Kind" {...field}>
                  {(Object.keys(METHOD_KINDS) as MethodKind[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                        <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                          {METHOD_ICONS[k]}
                        </Box>
                        <span>{METHOD_KINDS[k]}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="accountId"
              render={({ field, fieldState }) => (
                <TextField
                  select
                  label="Posts to account"
                  {...field}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ?? 'Where the money is held — cash in hand or a bank.'
                  }
                >
                  {options.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={save.isPending}>
            {method ? 'Save' : 'Add method'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
